import React from 'react';
import { GroupedVirtuoso } from 'react-virtuoso';
import { EMPTY_GROUP_SENTINEL_HEADER, QueueStore } from '../store/queue-store';
import { QueueRow } from './queue-row';
import { QueueVirtuosoListWrapper } from './queue-virtuoso-list-wrapper';
import { observer } from 'mobx-react-lite';
import { Flex, Grid, Box, InlineBanner, Text } from '@cad-arc';
import { useColumnDragState } from '../../common/hooks/use-column-drag-state';
import { RawQueueRowData } from '../store/types-2';
import { LoadingQueueRows } from './loading-queue-rows';
import { queueRowTestIds } from './test-ids';
import { errorOccurredWhile } from '../../common/helpers/error-occurred-while';

export const GroupedQueueRows = observer(function GroupedQueueRows({
    store,
    onRowDoubleClick,
    onClosePanel,
}: {
    store: QueueStore<RawQueueRowData>;
    onRowDoubleClick: (row: RawQueueRowData) => void;
    onClosePanel: () => void;
}): React.ReactElement {
    const { isDraggingColumn } = useColumnDragState();
    const data = store.listStore.data.list;
    const groupCounts = store.listStore.data.groupCounts;
    const groupHeaders = store.listStore.data.groupHeaders;
    const isLoading = store.isLoading;
    // This assignment dereferences the observable data.length which
    // subscribes this component to changes in the length of our list.
    // This makes us responsive to any new members in data, groupCounts,
    // or groupHeaders (because groupCounts and groupHeaders cannot have new members)
    // without there also being a change to the length of our list generally.
    // We use .length because it's an O(1) operation whereas alternatives like .slice()
    // are O(n)
    const _subscription = data.length;

    const renderGroupContent = (groupIndex: number): React.ReactElement => (
        <Grid
            key={
                groupHeaders[groupIndex] === EMPTY_GROUP_SENTINEL_HEADER
                    ? '(None)'
                    : groupHeaders[groupIndex]
            }
            templateColumns="var(--fullColumnWidth)"
            borderTop="3px solid var(--arc-colors-surface-accent)"
            marginTop="-1px"
        >
            <Box
                padding="0 5px"
                backgroundColor="surface.accent"
                w="fit-content"
                data-testid={queueRowTestIds.QUEUE_GROUP_HEADER}
                data-group-index={groupIndex}
            >
                <Text variant="headingXs">
                    {groupHeaders[groupIndex] === EMPTY_GROUP_SENTINEL_HEADER
                        ? '(None)'
                        : groupHeaders[groupIndex]}
                </Text>
            </Box>
        </Grid>
    );

    const renderItemContent = (index: number): React.ReactElement => {
        const item = data[index];
        return (
            <QueueRow
                key={item.id}
                index={index}
                data={item}
                onDoubleClick={onRowDoubleClick}
                isSelected={store.isSelected}
                onRowSelect={store.setSelectedRowId}
                queueColumns={store.workspacePanel.queue.queueColumns}
            />
        );
    };

    if (store.isError) {
        return (
            <Flex h="100%" justifyContent="center" alignItems="center">
                <InlineBanner
                    status="error"
                    title="An Error Occurred"
                    description={errorOccurredWhile('loading queue data')}
                    actions={[{ children: 'Close Panel', onClick: () => onClosePanel() }]}
                />
            </Flex>
        );
    }

    return isLoading ? (
        <LoadingQueueRows />
    ) : (
        <GroupedVirtuoso
            key="grouped-list"
            style={{
                height: '100%',
                width: '100%',
                overflowY: isDraggingColumn ? 'hidden' : 'auto',
            }}
            groupCounts={groupCounts}
            groupContent={renderGroupContent}
            components={{ List: QueueVirtuosoListWrapper }}
            overscan={200}
            itemContent={renderItemContent}
        />
    );
});
